import axios from "axios";

const API_BASE_URL = String(process.env.REACT_APP_PUBLIC_API_URL || "").trim();
const AUTH_STORAGE_KEY = "rfid_admin_auth";

const api = axios.create({ baseURL: API_BASE_URL, timeout: 10000, headers: { "Content-Type": "application/json" } });
api.interceptors.request.use((config) => {
  try {
    const auth = JSON.parse(sessionStorage.getItem(AUTH_STORAGE_KEY) || "null");
    if (auth?.access_token) config.headers.Authorization = `Bearer ${auth.access_token}`;
  } catch (_) {}
  return config;
});
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      try { sessionStorage.removeItem(AUTH_STORAGE_KEY); } catch (_) {}
      // A 3-hour admin JWT can expire while the page is still open. Returning
      // to AuthGate is clearer than leaving every button in a permanent 401 state.
      if (typeof window !== "undefined") window.location.reload();
    }
    return Promise.reject(error);
  }
);
const unwrap = (response) => response.data;

export const rfidAdminApi = {
  getDbHealth: () => api.get("/health/db").then(unwrap),
  compareSchool: (payload) => api.post("/api/v1/rfid/school/compare", payload).then(unwrap),
  getClasses: () => api.get("/api/v1/rfid/classes").then(unwrap),
  createClass: (payload) => api.post("/api/v1/rfid/classes", payload).then(unwrap),
  deleteClass: (id) => api.delete(`/api/v1/rfid/classes/${id}`).then(unwrap),
  getStudents: (classId) => classId ? api.get(`/api/v1/rfid/classes/${classId}/students`).then(unwrap) : Promise.resolve({ status: true, data: [] }),
  createStudent: (payload) => api.post("/api/v1/rfid/students", payload).then(unwrap),
  updateStudent: (id, payload) => api.patch(`/api/v1/rfid/students/${id}`, payload).then(unwrap),
  deleteStudent: (id) => api.delete(`/api/v1/rfid/students/${id}`).then(unwrap),
  getTags: () => api.get("/api/v1/rfid/tags").then(unwrap),
  createTag: (payload) => api.post("/api/v1/rfid/tags", payload).then(unwrap),
  updateTag: (id, payload) => api.patch(`/api/v1/rfid/tags/${id}`, payload).then(unwrap),
  deleteTag: (id) => api.delete(`/api/v1/rfid/tags/${id}`).then(unwrap),
  getUnregistered: (limit = 50) => api.get("/api/v1/rfid/unregistered", { params: { limit } }).then(unwrap),
  getLatestScan: (after) => api.get("/api/v1/rfid/latest-scan", { params: after ? { after } : {} }).then(unwrap),
  scanAssign: (payload) => api.post("/api/v1/rfid/scan-assign", payload).then(unwrap),
  createAssignment: (payload) => api.post("/api/v1/rfid/assignments", payload).then(unwrap),
  getAssignments: (classId) => classId ? api.get(`/api/v1/rfid/classes/${classId}/assignments`).then(unwrap) : Promise.resolve({ status: true, data: [] }),
  deleteAssignment: (id) => api.delete(`/api/v1/rfid/assignments/${id}`).then(unwrap),
  getReaders: () => api.get("/api/v1/rfid/readers").then(unwrap),
  createReader: (payload) => api.post("/api/v1/rfid/readers", payload).then(unwrap),
  updateReader: (id, payload) => api.patch(`/api/v1/rfid/readers/${id}`, payload).then(unwrap),
  rotateReaderKey: (id) => api.post(`/api/v1/rfid/readers/${id}/rotate-key`).then(unwrap),
  getIngestConfig: () => api.get("/api/v1/rfid/ingest-config").then(unwrap),
  getClassEvents: (classId, params = {}) => classId ? api.get(`/api/v1/rfid/classes/${classId}/events`, { params }).then(unwrap) : Promise.resolve({ status: true, data: [] }),
  getStudentEvents: (studentId, params = {}) => studentId ? api.get(`/api/v1/rfid/students/${studentId}/events`, { params }).then(unwrap) : Promise.resolve({ status: true, data: [] }),
  getClassSummary: (classId, params = {}) => classId ? api.get(`/api/v1/rfid/classes/${classId}/summary`, { params }).then(unwrap) : Promise.resolve({ status: true, data: [] }),
  getEvents: (params = {}) => api.get("/api/v1/rfid/events", { params }).then(unwrap),
  getTotalSummary: (params = {}) => api.get("/api/v1/rfid/summary", { params }).then(unwrap),
  resetAll: () => api.delete("/api/v1/rfid/reset").then(unwrap),
};

export const getData = (res) => res?.data || [];
export const isSuccess = (res) => res?.status === true || res?.success === true;

// React cannot render raw Error/Sequelize objects. API failures sometimes contain
// nested objects such as { name, parent, original }, so always reduce them to text.
export const toDisplayText = (value, fallback = "") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Error) return value.message || value.name || fallback;
  if (Array.isArray(value)) {
    const parts = value.map((item) => toDisplayText(item, "")).filter(Boolean);
    return parts.length ? parts.join(", ") : fallback;
  }
  if (typeof value === "object") {
    const candidates = [
      value.message,
      value.sqlMessage,
      value.detail,
      value.error,
      value.original?.message,
      value.parent?.message,
      value.original?.sqlMessage,
      value.parent?.sqlMessage,
      value.name,
    ];
    for (const candidate of candidates) {
      const text = toDisplayText(candidate, "");
      if (text) return text;
    }
    try {
      return JSON.stringify(value);
    } catch (_) {
      return fallback;
    }
  }
  return fallback;
};

export const getErrorMessage = (error, fallback = "요청 처리 중 오류가 발생했습니다.") => {
  if (error?.code === "ECONNABORTED") return "서버 응답 시간이 초과되었습니다.";
  const candidates = [
    error?.response?.data?.message,
    error?.response?.data?.error,
    error?.response?.data?.detail,
    error?.message,
    error,
  ];
  for (const candidate of candidates) {
    const text = toDisplayText(candidate, "");
    if (text) return text;
  }
  return fallback;
};
